import React from 'react'
import { ResponsiveTable, ResponsiveTableRow, Skeleton } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const atSizeColumns = (labels) => {
  return {
    default: {
      labels,
      columnWidths: [6, 6],
    },
  }
}

// Function to generate dynamic search URL
const generateRegionSearchUrl = (surname, regionName, country) => {
  if (!surname || !regionName || !country) {
    return null
  }

  const baseUrl = 'search/discovery/results/'
  const params = new URLSearchParams({
    tab: 'records',
    'q.surname': surname,
    'q.surname.exact': 'on',
    'q.anyPlace': `${regionName}, ${country}`,
    'q.anyPlace.exact': 'on',
    page: '1',
    results: '12',
  })

  return `${baseUrl}?${params.toString()}`
}

export default function TopRegionsTable({
  loading = false,
  topRegions = [],
  regionType = 'provinces', // e.g., 'provinces', 'states', 'prefectures'
  onRegionClick,
  showRecordCount = true,
  surname,
  country,
}) {
  const { t } = useTranslation()

  const labels = [
    t(`apac.section-templates.top-regions-table.region-header`, 'Top {count} {regionType}', {
      count: topRegions.length,
      regionType: t(`apac.section-templates.top-regions-table.${regionType}`, regionType),
    }),
    t('apac.section-templates.top-regions-table.records-header', 'Number of Records'),
  ]

  if (loading) {
    return <TopRegionsTableSkeleton labels={labels} />
  }

  if (!topRegions || topRegions.length === 0) {
    return null
  }

  return (
    <ResponsiveTable verticalAlign="middle" atSizeColumns={atSizeColumns(labels)} totalColumnCount={2}>
      {topRegions.map((region, index) => (
        <RegionRow
          key={region.code || region.name || index}
          region={region}
          index={index}
          onRegionClick={onRegionClick}
          showRecordCount={showRecordCount}
          surname={surname}
          country={country}
        />
      ))}
    </ResponsiveTable>
  )
}

function RegionRow({ region, index, onRegionClick, showRecordCount, surname, country }) {
  const { name, count, url } = region
  const displayName = name.slice(0, 1).toLocaleUpperCase() + name.slice(1)
  const displayCount = showRecordCount && count ? count.toLocaleString() : ''

  // Generate dynamic URL or use provided URL
  const dynamicUrl = generateRegionSearchUrl(surname, name, country)
  const finalUrl = url || dynamicUrl

  const handleClick = () => {
    if (onRegionClick) {
      onRegionClick(region)
    }
  }

  return (
    <ResponsiveTableRow
      data-testid={`top-region-${index + 1}`}
      to={finalUrl}
      external={finalUrl ? true : undefined}
      onClick={finalUrl ? undefined : handleClick}
      linkName={finalUrl ? `Link:Region Records - ${displayName}:Click` : undefined}
      noBottomDivider
      column1={displayName}
      column2={displayCount}
    />
  )
}

export function TopRegionsTableSkeleton({ labels }) {
  return (
    <ResponsiveTable verticalAlign="middle" atSizeColumns={atSizeColumns(labels)} totalColumnCount={2}>
      {Array.from({ length: 5 }).map((_, index) => (
        <ResponsiveTableRow
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          noBottomDivider
          column1={<Skeleton.Paragraph size="md" />}
          column2={<Skeleton.Paragraph size="md" />}
        />
      ))}
    </ResponsiveTable>
  )
}

TopRegionsTable.propTypes = {
  loading: PropTypes.bool,
  topRegions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number,
      code: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  regionType: PropTypes.string,
  onRegionClick: PropTypes.func,
  showRecordCount: PropTypes.bool,
  surname: PropTypes.string,
  country: PropTypes.string,
}

TopRegionsTableSkeleton.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
}
